import type { DTOImageMessageSendRequest } from 'src/apis/dto/DTOImageMessageSendRequest';
import type { DTOMediaInfo } from 'src/apis/dto/DTOMediaInfo';
import type { DTOMessageAckRequest } from 'src/apis/dto/DTOMessageAckRequest';
import type { DTOMessageGetRequest } from 'src/apis/dto/DTOMessageGetRequest';
import type { DTOMessageGetResponse } from 'src/apis/dto/DTOMessageGetResponse';
import type { DTOMessageInfo } from 'src/apis/dto/DTOMessageInfo';
import type { DTOMessageThreadConfig } from 'src/apis/dto/DTOMessageThreadConfig';
import type { DTOMessageThreadInfo } from 'src/apis/dto/DTOMessageThreadInfo';
import type { DTOOpenMessageThreadRequest } from 'src/apis/dto/DTOOpenMessageThreadRequest';
import type { DTOOpenMessageThreadResponse } from 'src/apis/dto/DTOOpenMessageThreadResponse';
import type { DTOSendImageResult } from 'src/apis/dto/DTOSendImageResult';
import type { DTOTextMessageSendRequest } from 'src/apis/dto/DTOTextMessageSendRequest';

import { APIClientType } from '.';

export interface MessagingServiceType {
  /**
   * 유저의 대화방(MessageThread)의 목록을 반환한다. 반환되는 대화방 목록은 마지막으로 메시징(텍스트 메시지, 비디오콜
   * 등)이 있었던 순서대로 정렬된다.
   * <p/>
   *
   * 비용이 클 수 있으므로 클라이언트에서는 매번 대화방 목록 확인 시 마다 본 메소드를 호출해서는 안되며 앱 실행 후 처음으로 대화방
   * 목록을 조회하거나 GCM으로 새로운 대화방이 생겼다는 이벤트를 받은 경우에만 다시 호출해야 한다.
   * <p/>
   *
   * @return 대화방의 정보를 담은 대화방정보DTO({@link MessageThreadInfo}) 배열
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   * @see MessageThreadInfo
   */
  listMessageThreads: () => Promise<DTOMessageThreadInfo[]>;

  /**
   * 유저의 대화방(MessageThread)들 중 변경이 있는 대화방, 즉 새로운 대화방 혹은 새로운 메시지가 있는 대화방의 개수을
   * 반환한다.
   * <p/>
   *
   * 클라이언트는 GCM으로 새로운 대화방이 생겼다는 이벤트를 받은 경우에 본 메소드를 다시 호출하여 UI 상에 새로운 값을 표시하여야
   * 한다.
   * <p/>
   *
   * @return 즉 새로운 대화방 혹은 새로운 메시지가 있는 대화방의 개수
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  countUpdatedMessageThreads: () => Promise<number>;

  /**
   * 지정한 대화방의 정보를 반환한다.
   * <p/>
   *
   * GCM으로 전달된 메시지를 통한 알림항목을 클릭해서 직접 대화방으로 들어오는 경우 해당 대화방의 정보를 얻기 위해 사용되며, 대화방
   * 목록에서 대화방에 입장하는 경우에도 목록 조회시의 대화방정보 데이터가 오래된 값일 수 있으므로 새롭게 대화방정보DTO를 얻기 위해
   * 본 메소드를 사용한다.
   * <p/>
   *
   * 대화방에 입장한 경우, {@link MessageThreadInfo#brokerChannelName}을 이용하여 브로커에 subscribe 할 수 있다.
   * subscribe 된 경우, 친구에게 받는 메시지 및 읽음 확인 등이 브로커를 통해 전달되고, 이 값은 GCM/APNS 푸시와 중복 될 수 있다.
   *
   * @param messageThreadId
   *            정보를 조회하고자 하는 대화방의 messageThreadId
   * @throws IllegalArgumentException
   *             주어진 messageThreadId에 해당하는 대화방이 없는 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  getMessageThread: (messageThreadId: string) => Promise<DTOMessageThreadInfo>;

  /**
   * 주어진 친구와 새로운 대화방을 생성한다.
   * <p/>
   *
   * 서버 상에서 새로운 대화방을 생성한 다음 해당 대화방의 정보를 반환한다. 본 메소드의 반환값은
   * {@link #getMessageThread(String)}와 동일하며 클라이언트는 대화방이 정상생성되는 경우 대화방에 입장하는
   * 것과 동일하게 처리할 수 있다.
   * <p/>
   *
   * 대화방에 입장한 경우, {@link MessageThreadInfo#brokerChannelName}을 이용하여 브로커에 subscribe 할 수 있다.
   * subscribe 된 경우, 친구에게 받는 메시지 및 읽음 확인 등이 브로커를 통해 전달되고, 이 값은 GCM/APNS 푸시와 중복 될 수 있다.
   *
   * @param friendId
   *            새로운 대화방을 생성하고자 하는 친구의 friendId
   * @throws IllegalArgumentException
   *             주어진 friendId에 해당하는 친구가 없는 경우
   * @throws IllegalStateException
   *             주어진 friendId에 해당하는 친구와 이미 대화방이 있는 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  createMessageThread: (friendId: string) => Promise<DTOMessageThreadInfo>;

  /**
   * 지정한 대화방을 닫는다.
   * <p/>
   *
   * 대화방을 닫으면 더이상 {@link #listMessageThreads()}를 통해 대화방 목록을 조회할 때 반환되지 않는다. 단
   * 친구인 상대와의 대화방인지 친구가 아닌 상대와의 대화방인지에 따라 서버상의 처리가 달라지는데 그 차이는 아래와 같다.
   *
   * <ul>
   * <li>친구와의 대화방인 경우: 해당 대화방을 일시적으로 내 대화방 목록에서 제외시킨다. 상대유저의 대화방 목록에서의 대화방은
   * 닫히지 않고 그대로 유지되며 상대가 새로운 메시지를 보내게 되면 내 대화방 목록에 새롭게 나타나게 된다.</li>
   * <li>친구가 아닌 상대와의 대화방인 경우: 해당 대화방을 영원히 내 대화방 목록에서 제외시킨다. 상대유저에게도 대화방은 닫힌
   * 상태가 되며 상대유저는 해당 대화방을 통해 추가적인 메시지를 보낼 수 없게 된다. 친구신청은 자동적으로 취소(내가 신청한 경우) 및
   * 거절(상대가 신청한 경우) 처리된다.</li>
   * </ul>
   *
   * 친구목록에서 {@link FriendService#deleteFriend(String)}를 통해 친구를 지우면 해당친구와의 대화방은
   * 본 메소드가 불린 것과 동일하게 자동으로 닫히게 된다.
   * <p/>
   *
   * @param messageThreadId
   *            닫을 대화방의 messageThreadId
   * @throws IllegalArgumentException
   *             주어진 messageThreadId에 해당하는 대화방이 없는 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  closeMessageThread: (messageThreadId: string) => Promise<void>;

  /**
   * 지정한 대화방의 설정 정보를 변경한다.
   * <p/>
   *
   * @param messageThreadId
   *            설정 정보를 변경할 대화방의 messageThreadId
   * @param messageThreadConfig
   *            새롭게 변경할 설정정보
   * @throws IllegalArgumentException
   *             주어진 messageThreadId에 해당하는 대화방이 없는 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  configureMessageThread: (
    messageThreadId: string,
    messageThreadConfig: DTOMessageThreadConfig
  ) => Promise<void>;

  /**
   * 지정한 대화방 내에서 텍스트메시지를 전송한다.
   * <p/>
   *
   * 내가 전송한 텍스트메시지는 상대방에게 메시지수신됨 GCM 이벤트로 실시간으로 전달된다. 각 텍스트메시지는 하나의 GCM 메시지의
   * payload 안에 담겨 하기 때문에 UTF-8인코딩과 기타 오버헤드를 포함하여 최대 1024 글자로 제한한다.
   * <p/>
   *
   * 클라이언트는 각 메시지마다 고유한 UUID를 포함하여 송신하여 서버가 메시지를 중복으로 받아들였는지 여부를 판별할 수 있도록
   * 해야한다.
   * <p/>
   *
   * 서버에서는 수신한 요청을 바탕으로 메시지를 저장한 후 해당 메시지를 나타내는 {@link MessageInfo}를 작성한
   * 다음 메시지를 전송한 유저에게 반환한다. 그리고 메시지를 수신할 상대유저에 {@link GcmTextMessage} GCM 메시지를
   * 보낸다. 상대유저가 친구라면 소리/진동이 켜진 상태로 전달되며, 친구가 아니라면 소리/진동이 꺼진 상태로 전달된다.
   *
   * @param textMessageSendRequest
   *            전송할 텍스트메시지를 나타내는 TextMessageSendRequest 객체
   * @return 전송된 메시지를 나타내는 MessageInfo 객체
   * @throws IllegalArgumentException
   *             주어진 textMessageSendRequest의 값이 잘못된 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   * @throws AzarFeedbackException 메세지 일시정지를 당했는데 메세지를 보내는 경우.
   */
  sendTextMessage: (textMessageSendRequest: DTOTextMessageSendRequest) => Promise<DTOMessageInfo>;

  /**
   * 지정한 대화방 내에서 이미지 메시지를 전송한다.
   * <p/>
   * 상호친구인 경우에만 이미지를 보낼 수 있다. 클라이언트에서는 {@link MessageThreadInfo#textOnly} 가 false 인 경우에만
   * 이미지를 보내야 한다.
   * <p/>
   * 전송은 비동기적으로 이루어지며, 완료된 경우 안드로이드는 {@link com.azarlive.api.event.gcm.GcmMessageSent}, iOS는
   * {@link com.azarlive.api.event.apns.ApnsMessageSent} 가 푸시 메시지로서 전송된다.
   * 푸시 메커니즘은 안정성을 보장할 수 없기 때문에 클라이언트는 푸시 뿐만 아니라 {@link #pollSendImageResultV2(String)} 을 이용하여
   * 주기적으로 결과를 확인하여야 한다.
   * 보낼 이미지는 <code>max(width, height) &lt= 1280</code> 이어야 하며, 이보다 큰 경우 스케일링된다. 파일 크기가 100kb 를 넘어가는
   * 경우에는 썸네일을 만들어서 별도로 업로드하게 된다.
   * <p/>
   * 상세사항은 {@link #sendTextMessage(TextMessageSendRequest)} 참조.
   *
   * @param imageMessageSendRequest 전송할 이미지 메시지를 나타내는 객체
   * @throws IllegalArgumentException 요청값이 잘못된 경우
   * @throws AuthenticationException  로그인 상태가 아닌 경우
   * @throws AzarFeedbackException 메세지 일시정지를 당했는데 메세지를 보내는 경우.
   */
  sendImageMessage: (imageMessageSendRequest: DTOImageMessageSendRequest) => Promise<void>;

  /**
   * 자신이 보낸 이미지 메시지의 전송 결과를 확인한다.
   * SendImageResult의 sent가 true일 경우 전송 완료된 상태로 MessageInfo가 제공되며,
   * false인 경우 아직 전송 중인 상태이다.
   * messageUid가 유효하지 않은 경우, 전송 과정에서 오류가 발생한 경우, 시간 내에 전송하지 못한 경우 IllegalArgumentException이 발생함.
   * 한번이라도 IllegalArgumentException이 발생했을 경우 전송을 실패한 것으로 생각한다.
   *
   * @param messageUid 전송 요청시 지정한 {@link ImageMessageSendRequest#messageUid}
   * @return 이미지 메세지 전송 결과
   * @throws IllegalArgumentException 전송 실패
   * @throws AuthenticationException 로그인 상태가 아닌 경우
   */
  pollSendImageResultV2: (messageUid: string) => Promise<DTOSendImageResult>;

  /**
   * 지정한 대화방 내에서 주어진 범위의 메시지를 얻어온다. {@link #getTextMessage} 와는 달리 타이틀, 이미지, 링크 등을 표시할 수 있는
   * 멀티미디어 메시지를 지원한다.
   * <p/>
   *
   * 클라이언트에서 유저가 대화방에 입장하게 되면 {@link #getMessageThread(String)}를 통해 얻어온
   * lastMessageSeqNo와 로컬 sqlite DB에 저장된 마지막 messageSeqNo를 비교하여 서버 상에 아직 받아오지
   * 않은 메시지가 존재하는 경우 본 메소드를 호출하여 최신 메시지를 얻어온다.
   * <p/>
   *
   * 또한 GCM 이벤트 혹은 {@link #sendTextMessage(TextMessageSendRequest)}의 결과로 얻어온
   * {@link MessageInfo}의 messageSeqNo의 값과 로컬에 저장된 마지막 messageSeqNo 사이에 빈
   * 값이 있는 경우에도 최신 메시지를 얻어오기 위해 본 메소드를 사용한다. (단, 결번체크 로직은 미묘한 딜레이는 무시할 수 있도록
   * 설계되어야 한다)
   * <p/>
   *
   * 본 메소드를 통해서 얻어온 상대방 메시지에 대해서는 자동으로 수신처리가 되므로 별도로
   * {@link #ackTextMessage(MessageAckRequest)}를 호출할 필요는 없다.
   * 단, {@link MessageGetRequest#noAck} 가 true 인 경우에는 수신처리를 하지 않는다.
   *
   * @param messageGetRequest
   *            얻어올 메시지의 범위를 나타내는 MessageGetRequest 객체
   * @return 얻어온 텍스트메시지를 나타내는 TextMessageInfo의 배열
   * @throws IllegalArgumentException
   *             주어진 TextMessageGetRequest의 값이 잘못된 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   */
  getMessage: (messageGetRequest: DTOMessageGetRequest) => Promise<DTOMessageGetResponse>;

  /**
   * 미디어 타입이 {@link MediaInfo#TYPE_IMAGE_THUMBNAIL}인 경우, 풀사이즈 이미지 정보를 얻어온다.
   *
   * @param messageThreadId 대상 메시지가 속한 스레드의 messageThreadId
   * @param messageSeqNo 대상 메시지의 시퀀스 번호
   * @return 얻어온 풀사이즈 이미지 정보
   * @throws IllegalArgumentException 인수가 잘못된 경우
   * @throws AuthenticationException 로그인 상태가 아닌 경우
   * @since 164
   */
  getFullSizeImageInfo: (messageThreadId: string, messageSeqNo?: number) => Promise<DTOMediaInfo>;

  /**
   * GCM을 통해 상대방의 텍스트메시지를 수신했음을 서버에 확인한다.
   * <p/>
   *
   * 클라이언트에서 유저가 대화방 내부에 있는 상황에서 GCM으로 상대방의 텍스트메시지를 수신한 경우 이미 메시지 본문이 GCM
   * payload에 실려 있으므로 별도로 {@link #getTextMessage(TextMessageGetRequest)}를 호출할
   * 필요가 없다. 하지만 상대방에게 메시지를 정상적으로 읽었음을 확인하기 위해 마지막으로 수신한 메시지의 일련번호를 본 메소드를 통해
   * 서버에 알린다.
   * <p/>
   *
   * (GCM 메시지를 짧은 시간 안에 중복하여 수신한 경우) 너무 많은 요청을 서버에 보내는 것을 방지하기 위하여 GCM 메시지 수신
   * 후 본 메소드를 즉시 호출해서는 안되며 특정시간 (1~3초?) 동안 대기한 다음 중복으로 수신되는 GCM 메시지 중 마지막 수신건에
   * 대해서만 본 메소드를 호출해야 한다.
   * <p/>
   *
   * 메시지를 보낸 상대방에게는 {@link GcmMessageAcked} GCM 이벤트가 전달된다.
   * <p/>
   *
   * @param textMessageAckRequest
   *            수신확인할 메시지를 나타내는 TextMessageAckRequest 객체
   * @throws IllegalArgumentException
   *             주어진 TextMessageAckRequest의 값이 잘못된 경우
   * @throws AuthenticationException
   *             로그인 상태가 아닌 경우
   *
   * @see MessageAckRequest
   */
  ackMessage: (textMessageAckRequest: DTOMessageAckRequest) => Promise<void>;

  /**
   * 아직 읽지 않은 메시지의 수를 가져온다.
   * APNS Push에서 사용하는 Badge Number와 같은 의미를 가진다.
   *
   * @return 안 읽은 메시지의 수.
   * @throws AuthenticationException 로그인 상태가 아님.
   */
  countBadgeNumber: () => Promise<number>;

  /**
   * 유료 채팅방 열기 (무료 메세지 아이템 사용 가능)
   * 요청한 사람 기준, 이미 채팅방이 열려 있는 경우 과금 없이 채팅방 정보 응답을 내려줌
   */
  openMessageThread: (data: DTOOpenMessageThreadRequest) => Promise<DTOOpenMessageThreadResponse>;
}

export class MessagingService implements MessagingService {
  private client: APIClientType;

  constructor(customClient: APIClientType) {
    this.client = customClient;
  }

  public listMessageThreads = async () => {
    return this.client.call<DTOMessageThreadInfo[], void>({
      method: 'listMessageThreads',
    });
  };

  public countUpdatedMessageThreads = async () => {
    return this.client.call<number, void>({
      method: 'countUpdatedMessageThreads',
    });
  };

  public getMessageThread = async (messageThreadId: string) => {
    return this.client.call<DTOMessageThreadInfo, Parameters<typeof this.getMessageThread>>({
      method: 'getMessageThread',
      data: [messageThreadId],
    });
  };

  public createMessageThread = async (friendId: string) => {
    return this.client.call<DTOMessageThreadInfo, Parameters<typeof this.createMessageThread>>({
      method: 'createMessageThread',
      data: [friendId],
    });
  };

  public closeMessageThread = async (messageThreadId: string) => {
    return this.client.call<void, Parameters<typeof this.closeMessageThread>>({
      method: 'closeMessageThread',
      data: [messageThreadId],
    });
  };

  public configureMessageThread = async (
    messageThreadId: string,
    messageThreadConfig: DTOMessageThreadConfig
  ) => {
    return this.client.call<void, Parameters<typeof this.configureMessageThread>>({
      method: 'configureMessageThread',
      data: [messageThreadId, messageThreadConfig] as const,
    });
  };

  public sendTextMessage = async (textMessageSendRequest: DTOTextMessageSendRequest) => {
    return this.client.call<DTOMessageInfo, Parameters<typeof this.sendTextMessage>>({
      method: 'sendTextMessage',
      data: [textMessageSendRequest],
    });
  };

  public sendImageMessage = async (imageMessageSendRequest: DTOImageMessageSendRequest) => {
    return this.client.call<void, Parameters<typeof this.sendImageMessage>>({
      method: 'sendImageMessage',
      data: [imageMessageSendRequest],
    });
  };

  public pollSendImageResultV2 = async (messageUid: string) => {
    return this.client.call<DTOSendImageResult, Parameters<typeof this.pollSendImageResultV2>>({
      method: 'pollSendImageResultV2',
      data: [messageUid] as const,
    });
  };

  public getMessage = async (messageGetRequest: DTOMessageGetRequest) => {
    return this.client.call<DTOMessageGetResponse, Parameters<typeof this.getMessage>>({
      method: 'getMessage',
      data: [messageGetRequest] as const,
    });
  };

  public getFullSizeImageInfo = async (messageThreadId: string, messageSeqNo?: number) => {
    return this.client.call<DTOMediaInfo, Parameters<typeof this.getFullSizeImageInfo>>({
      method: 'getFullSizeImageInfo',
      data: [messageThreadId, messageSeqNo] as const,
    });
  };

  public ackMessage = async (textMessageAckRequest: DTOMessageAckRequest) => {
    return this.client.call<void, Parameters<typeof this.ackMessage>>({
      method: 'ackMessage',
      data: [textMessageAckRequest] as const,
    });
  };

  public countBadgeNumber = async () => {
    return this.client.call<number, Parameters<typeof this.countBadgeNumber>>({
      method: 'countBadgeNumber',
    });
  };

  public openMessageThread = async (data: DTOOpenMessageThreadRequest) => {
    return this.client.call<DTOOpenMessageThreadResponse, DTOOpenMessageThreadRequest>({
      method: 'POST',
      path: '/api/v1/messages/open',
      data,
    });
  };
}
